import { useState, useEffect } from "react";
import { createAddon, getProducts } from "../../../Services/subscription";
import Loader from "react-js-loader";
import "./requestCoachingMinutes.css";
import TooltipContent from "../../../Components/Tooltip/ToolTipContent";

const RequestCoachingMinutes = ({
  isOpen,
  onClose,
  remainingMinutes,
  onSuccess,
  redirectUrl = '/dialogues'
}) => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [minuteOptions, setMinuteOptions] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState(null);

  // Fetch products and pricing on component mount
  useEffect(() => {
    if (isOpen) {
      fetchProductsAndPricing();
    }
  }, [isOpen]);

  const fetchProductsAndPricing = async () => {
    try {
      setLoading(true);
      const res = await getProducts();
      const list = Array.isArray(res?.payload?.products)
        ? res.payload.products
        : (Array.isArray(res?.payload) ? res.payload : []);
      // Find the coaching minutes product
      const coachingProduct = list.find(
        (product) => (product?.metadata?.db_plan_type=== "coaching")
      );
      if (coachingProduct) {
        const prices = Array.isArray(coachingProduct?.prices) ? coachingProduct.prices : [];
        const options = prices.map((p) => {
          // parse minutes from nickname like "60_min_coaching"
          let minutes;
          if (typeof p?.nickname === 'string') {
            const m = p.nickname.match(/(\d+)/);
            if (m) minutes = parseInt(m[1], 10);
          }

          return {
            id: p?.stripe_price_id,
            minutes: minutes || 0,
            price: (p?.unit_amount || 0) / 100,
            priceCents: p?.unit_amount || 0,
          };
        }).filter(o => o.minutes > 0);

        // Sort by minutes descending so best value at top
        options.sort((a, b) => b.minutes - a.minutes);
        setMinuteOptions(options);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load coaching minute options. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
  };

  const handlePurchase = async () => {
    if (!selectedPlan) return;

    try {
      setLoading(true);
      const profileContent = JSON.parse(localStorage.getItem("profile"));

      const checkoutUrl = `${window.location.origin}${redirectUrl}`;
      const payload = {
        payment_method: "card",
        users: [
          {
            price_id: selectedPlan.id,
            user_id: profileContent?._id,
            value: "",
            quantity: 1,
          },
        ],
        // Additional checkout parameters
        cancel_url: checkoutUrl,
        success_url: checkoutUrl,
        mode: "payment",
        price_id: selectedPlan.id,
        quantity: 1,
      };

      const response = await createAddon(payload);
      if (response?.url) {
        // Redirect to checkout - don't close modal or set loading to false
        // The page will navigate away, so no cleanup needed
        window.location.href = response.url;
        return; // Exit early, don't run finally block
      } else {
        // No URL returned - show error
        setError("Failed to create checkout session. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error purchasing minutes:", error);
      setError("Something went wrong. Please try again later.");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="purchase-modal-container">
      <header className="modal-header">
        <div className="header-text">
          <h1 className="title">Purchase Dialogue Minutes</h1>
          <p className="subtitle">You have {remainingMinutes} minutes remaining</p>
        </div>
        <button className="close-button" onClick={onClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </header>

      <main className="modal-content-coaching">
        <p className="content-title">Select an M-Pack:</p>
        {loading ? (
          <div className="loading-container">
            <Loader
              type="spinner-cub"
              bgColor={"#3D6DEB"}
              color={"#3D6DEB"}
              size={30}
            />
          </div>
        ) : (
          <div className="options-list">
            {minuteOptions.map((option) => (
              <label
                key={option.id}
                className={`option-item ${selectedPlan?.id === option.id ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="minutes-option"
                  className="radio-input"
                  checked={selectedPlan?.id === option.id}
                  onChange={() => handlePlanSelection(option)}
                />
                <span className="radio-custom"></span>
                <div className="option-details">
                  <span className="option-main-text">
                    <strong>M-Pack {option.minutes}</strong> — {option.minutes} minutes for ${option.price?.toLocaleString("en-US", { maximumFractionDigits: 2 })}
                  </span>
                </div>
              </label>
            ))}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
      </main>

      <footer className="modal-footer">
        <button
          className={`btn btn-primary ${!selectedPlan || loading ? 'disabled' : ''}`}
          onClick={handlePurchase}
          disabled={!selectedPlan || loading}
        >
          {loading ? (
            <Loader
              type="spinner-cub"
              bgColor={"#fff"}
              color={"#3D6DEB"}
              size={20}
            />
          ) : 'Purchase M-Pack'}
        </button>
        <button
          className="btn btn-secondary"
          onClick={onClose}
          disabled={loading}
        >
          Nevermind
        </button>
      </footer>
    </div>
  );
};

export default RequestCoachingMinutes;
